import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  input,
  Input,
  OnChanges,
  signal,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { SelectModule } from 'primeng/select';
import {
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { ShadowDomStyleInjectorService } from '@everymatrix-base-components/utils';
import { NgStyle } from '@angular/common';

@Component({
  selector: 'lib-dm-select-lib',
  imports: [SelectModule, MultiSelectModule, ReactiveFormsModule, NgStyle],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DmSelectLibComponent),
      multi: true,
    },
  ],
  templateUrl: './dm-select-lib.component.html',
  styleUrl: './dm-select-lib.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class DmSelectLibComponent implements OnChanges, AfterViewInit {
  /**
   * Label text displayed above the input
   */
  @Input() label = '';

  /**
   * Placeholder text inside the input
   */
  @Input() placeholder = '';

  /**
   * Disables the input field if set to true
   */
  @Input({ transform: booleanAttribute }) disabled = false;

  /**
   * Marks the input as required if set to true
   */
  @Input({ transform: booleanAttribute }) isRequired = false;

  /**
   * Determines whether to use multiple selection
   */
  @Input({ transform: booleanAttribute }) isMultiSelect = false;

  /**
   * When specified, displays an input field to filter the items on keyup.
   */
  @Input({ transform: booleanAttribute }) filter = true;

  /**
   * Initial value provided by the parent component
   * Usage: pass a simple string (key value) for single select and for multiselect pass a stringified array of key values.
   */
  @Input() set value(data: string) {
    if (data && JSON.stringify(this.selectControl.value) !== data) {
      let parsedValue: string | string[];
      try {
        parsedValue = JSON.parse(data);
      } catch (e) {
        parsedValue = data;
      }
      this.selectControl.setValue(parsedValue);
    }
  }

  /**
   * An array of objects to display as the available options.
   * Usage:
   * - Pass an array of objects directly, for example:
   *   [
   *     { label: 'Test 1', value: 'test1' },
   *     { label: 'Test 2', value: 'test2' },
   *     { label: 'Test 3', value: 'test3' },
   *     // ...
   *   ]
   *
   * - Alternatively, pass a JSON stringified array of objects, for example:
   *   '[{"label":"Test 1","value":"test1"},{"label":"Test 2","value":"test2"},{"label":"Test 3","value":"test3"}]'
   */
  @Input() set options(value: any) {
    if (value && typeof value === 'string') {
      this.selectOptions = JSON.parse(value);
    } else {
      this.selectOptions = value;
    }
  }

  /**
   * Name of the value field of an option.
   */
  @Input() optionValue = 'value';

  /**
   * Name of the label field of an option.
   */
  @Input() optionLabel = 'label';

  /**
   * Name of the disabled field of an option.
   */
  @Input() optionDisabled = '';

  /**
   * Text to display when filtering does not return any results. Defaults to global value in i18n translation configuration.
   */
  @Input() emptyFilterMessage = '';

  /**
   * Filter placeholder
   */
  @Input() filterPlaceHolder = 'Search';

  /**
   * Loading state
   */
  @Input() loading = false;

  /**
   * Hint message
   */
  hint = input<string>('');

  /**
   * Always show hint
   */
  permanentHint = input<boolean>(false);

  selectOptions!: any[];
  selectedItemsLabel!: string;
  selectControl: FormControl<any> = new FormControl<any>('');
  isDropdownOpen = false;
  isFocused = false;
  showClear = true;
  attachedToShadowDom = signal(false);

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private elementRef: ElementRef,
    private shadowDomStyleInjectorService: ShadowDomStyleInjectorService
  ) {
    this.selectControl.valueChanges.subscribe((value) => {
      if (value) {
        this.showClear = true;
        this.selectedItemsLabel = `Selected ${this.label}: ${value.length}`;
      }

      this.onChange(value);
      this.onTouched();
      this.elementRef.nativeElement.dispatchEvent(
        new CustomEvent('dm-select-value-changed', {
          detail: value,
          bubbles: true,
          composed: true,
        })
      );
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isRequired']) {
      if (this.isRequired) {
        this.selectControl.setValidators(Validators.required);
      } else {
        this.selectControl.clearValidators();
      }
      this.selectControl.updateValueAndValidity();
    }
  }

  ngAfterViewInit() {
    this.shadowDomStyleInjectorService
      .injectNgPrimeStyles(this.elementRef)
      .subscribe((data) => this.attachedToShadowDom.set(data));
  }

  writeValue(value: any): void {
    this.selectControl.setValue(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(): void {
    if (this.disabled) {
      this.selectControl.disable();
    } else {
      this.selectControl.enable();
    }
  }
}
